const ROOT_SELECTOR = '[data-nds-product-models]'
const FACET_LIMIT = 8
const STRIPE_CLASS = 'u-bg-color-gray-light'
const VALUE_DELIMITER = '|'
const SIDEBAR_QUERY = '(min-width: 1025px)'

function normalize(value) {
    return (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[™®©]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
}

function tokenize(query) {
    const normalized = normalize(query)
    return normalized ? normalized.split(' ') : []
}

function pluralize(count, noun) {
    return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`
}

function setup(root) {
    if (root.dataset.pmReady === 'true') return
    root.dataset.pmReady = 'true'

    const searchInput = root.querySelector('[data-pm-search]')
    const clearSearchButton = root.querySelector('[data-pm-clear-search]')
    const countEl = root.querySelector('[data-pm-count]')
    const refinementsRow = root.querySelector('[data-pm-refinements]')
    const refinementsRoot = refinementsRow?.querySelector('.ais-CurrentRefinements')
    const refinementList = root.querySelector('[data-pm-refinement-list]')
    const clearRefinementsButton = root.querySelector('[data-pm-clear-refinements]')
    const noResults = root.querySelector('[data-pm-no-results]')
    const noResultsTitle = root.querySelector('[data-pm-no-results-title]')
    const noResultsHint = root.querySelector('[data-pm-no-results-hint]')
    const facetsDetails = root.querySelector('[data-pm-facets]')

    const facets = Array.from(root.querySelectorAll('[data-pm-facet]')).map((panel) => ({
        key: panel.dataset.pmFacet,
        label: panel.querySelector('.ais-Panel-header')?.textContent.trim() ?? '',
        panel,
        showMore: panel.querySelector('[data-pm-show-more]'),
        expanded: false,
        selected: new Set(),
        options: Array.from(panel.querySelectorAll('[data-pm-value]')).map((item) => ({
            item,
            value: item.dataset.pmValue,
            label: item.querySelector('.ais-RefinementList-labelText')?.textContent.trim() ?? '',
            input: item.querySelector('input[type="checkbox"]'),
            countEl: item.querySelector('.ais-RefinementList-count'),
        })),
    }))

    const rows = Array.from(root.querySelectorAll('[data-pm-row]')).map((el) => ({
        el,
        id: el.dataset.productId || el.dataset.sku || el.textContent,
        text: normalize(el.textContent),
        values: Object.fromEntries(
            facets.map((f) => [f.key, (el.getAttribute(`data-${f.key}`) || '').split(VALUE_DELIMITER).filter(Boolean)]),
        ),
    }))

    const tables = Array.from(root.querySelectorAll('[data-pm-table]')).map((el) => ({
        el,
        rows: rows.filter((row) => el.contains(row.el)),
    }))

    const totalProducts = new Set(rows.map((r) => r.id)).size
    let tokens = []

    function matches(row, ignoreFacetKey = null) {
        for (const token of tokens) {
            if (!row.text.includes(token)) return false
        }
        for (const facet of facets) {
            if (facet.key === ignoreFacetKey || facet.selected.size === 0) continue
            if (!row.values[facet.key].some((value) => facet.selected.has(value))) return false
        }
        return true
    }

    function hasRefinements() {
        return facets.some((f) => f.selected.size > 0)
    }

    function updateRows() {
        const visibleIds = new Set()

        for (const table of tables) {
            let stripe = 0
            for (const row of table.rows) {
                const visible = matches(row)
                row.el.hidden = !visible
                if (visible) {
                    row.el.classList.toggle(STRIPE_CLASS, stripe % 2 === 1)
                    stripe++
                    visibleIds.add(row.id)
                }
            }
            table.el.hidden = stripe === 0
        }

        return visibleIds.size
    }

    function updateFacets() {
        for (const facet of facets) {
            const counts = new Map()
            const counted = new Set()

            for (const row of rows) {
                const values = row.values[facet.key]
                if (values.length === 0 || !matches(row, facet.key)) continue
                for (const value of values) {
                    const dedupeKey = `${row.id}|${value}`
                    if (counted.has(dedupeKey)) continue
                    counted.add(dedupeKey)
                    counts.set(value, (counts.get(value) || 0) + 1)
                }
            }

            let shown = 0
            let available = 0
            for (const option of facet.options) {
                const count = counts.get(option.value) || 0
                const selected = facet.selected.has(option.value)
                const offered = count > 0 || selected

                option.input.checked = selected
                option.item.classList.toggle('ais-RefinementList-item--selected', selected)
                if (option.countEl) option.countEl.textContent = count.toLocaleString()

                if (offered) available++
                const withinLimit = facet.expanded || shown < FACET_LIMIT
                option.item.hidden = !(offered && withinLimit)
                if (offered && withinLimit) shown++
            }

            facet.panel.hidden = available === 0

            if (facet.showMore) {
                facet.showMore.hidden = available <= FACET_LIMIT
                facet.showMore.textContent = facet.expanded ? 'Show less' : 'Show more'
                facet.showMore.setAttribute('aria-expanded', String(facet.expanded))
            }
        }
    }

    function updateRefinements() {
        if (!refinementList) return

        refinementList.replaceChildren()

        for (const facet of facets) {
            if (facet.selected.size === 0) continue

            const item = document.createElement('li')
            item.className = 'ais-CurrentRefinements-item'

            const label = document.createElement('span')
            label.className = 'ais-CurrentRefinements-label'
            label.textContent = `${facet.label}:`
            item.appendChild(label)

            for (const option of facet.options) {
                if (!facet.selected.has(option.value)) continue

                const chip = document.createElement('span')
                chip.className = 'ais-CurrentRefinements-category'

                const chipLabel = document.createElement('span')
                chipLabel.className = 'ais-CurrentRefinements-categoryLabel'
                chipLabel.textContent = option.label
                chip.appendChild(chipLabel)

                const remove = document.createElement('button')
                remove.type = 'button'
                remove.className = 'ais-CurrentRefinements-delete'
                remove.setAttribute('aria-label', `Remove ${facet.label} filter: ${option.label}`)
                remove.textContent = '✕'
                remove.addEventListener('click', () => {
                    facet.selected.delete(option.value)
                    update()
                })
                chip.appendChild(remove)

                item.appendChild(chip)
            }

            refinementList.appendChild(item)
        }

        refinementsRoot?.classList.toggle('ais-CurrentRefinements--noRefinement', !hasRefinements())
    }

    function updateStatus(visibleCount) {
        const filtered = tokens.length > 0 || hasRefinements()

        if (countEl) {
            countEl.textContent = filtered
                ? `Showing ${visibleCount.toLocaleString()} of ${pluralize(totalProducts, 'product')}`
                : pluralize(totalProducts, 'product')
        }

        if (noResults) {
            noResults.hidden = visibleCount > 0
            if (visibleCount === 0 && noResultsTitle) {
                const query = searchInput?.value.trim()
                noResultsTitle.replaceChildren()
                if (query) {
                    noResultsTitle.append('No results for ')
                    const strong = document.createElement('strong')
                    strong.textContent = query
                    noResultsTitle.append(strong)
                } else {
                    noResultsTitle.append('No results found.')
                }
            }
            if (noResultsHint) noResultsHint.hidden = !hasRefinements()
        }

        if (clearSearchButton) clearSearchButton.hidden = !searchInput?.value
    }

    function update() {
        tokens = tokenize(searchInput?.value)
        const visibleCount = updateRows()
        updateFacets()
        updateRefinements()
        updateStatus(visibleCount)
    }

    if (searchInput) {
        searchInput.addEventListener('input', update)
        searchInput.addEventListener('search', update)
        searchInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') event.preventDefault()
        })
    }

    clearSearchButton?.addEventListener('click', () => {
        searchInput.value = ''
        update()
        searchInput.focus()
    })

    for (const facet of facets) {
        for (const option of facet.options) {
            option.input?.addEventListener('change', () => {
                if (option.input.checked) facet.selected.add(option.value)
                else facet.selected.delete(option.value)
                update()
            })
        }

        facet.showMore?.addEventListener('click', () => {
            facet.expanded = !facet.expanded
            update()
        })
    }

    clearRefinementsButton?.addEventListener('click', () => {
        facets.forEach((f) => f.selected.clear())
        update()
    })

    if (facetsDetails && window.matchMedia) {
        const mql = window.matchMedia(SIDEBAR_QUERY)
        const sync = () => { facetsDetails.open = mql.matches }
        sync()
        mql.addEventListener?.('change', sync)
    }

    for (const facet of facets) {
        for (const option of facet.options) {
            if (option.input?.checked) facet.selected.add(option.value)
        }
    }

    update()
}

function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(setup)
}

export { init }
